/***
 *
 *   TICKET DETAILS
 *   Two-pane drawer. The main column scrolls the description and conversation
 *   between a fixed header and a fixed composer. The right rail keeps
 *   attachments, properties, requester and actions in view and scrolls on its
 *   own.
 *
 *   Replaces the Details / Activity tab split: comments and activity are the
 *   reason this drawer gets opened, so they are no longer one click away, and
 *   nothing they need for context disappears when you go to read them. Below
 *   `lg` the rail folds into the scroll, above the conversation — it is
 *   reference material there, not something you read against.
 *
 **********/

import React, { useCallback, useEffect, useRef, useState } from 'react';
import Style from './details.tailwind';
import TicketHistory, { isComment } from './history';
import TicketDetailsNav from './nav';
import TicketDescription from './context';
import TicketAttachments, { splitAttachments } from './attachments';
import TicketGeneralInfo from './general';
import TicketOwner from './owner';
import TicketCommands from './commands';
import { Chat } from '../history/chat/chat';
import { useLiveQuery } from 'components/hooks/websocket';
import { useSearchParams } from 'react-router-dom';
import { findTicketStatus } from 'utilities/tickets';
import { useProjectContext } from 'context/ProjectContext';
import { useTranslate } from 'app/translations';
import { Button, Icon } from 'components/lib';
import { buildQueryParams } from 'utilities/params';

// `c` can be "all" or a comma-separated multi-select on the board; only a
// single id is a usable realtime channel.
const singleCategory = (value) => (value && value !== 'all' && !value.includes(',') ? value : undefined);

export const TicketDetails = () => {
    const { _t } = useTranslate();
    let [searchParams, setSearchParams] = useSearchParams();
    const { switchProject, selectedProject, projects } = useProjectContext();
    const scrollRef = useRef(null);

    const number = searchParams.get('id');
    const [channelCategory, setChannelCategory] = useState(() =>
        singleCategory(searchParams.get('c')),
    );

    // Fetch by number only: the board's category/user filters must never hide
    // the ticket being opened. The category is only used for the realtime channel.
    const { data, status, refetch, isFetching } = useLiveQuery(
        'tickets',
        {
            url: `/api/tickets${buildQueryParams({ number })}`,
            number,
            ticket_category_id: channelCategory,
            user_id: null,
        },
        { keepPreviousData: true },
    );

    const list = Array.isArray(data) ? data : null;
    const ticket = list?.find((item) => String(item.number) === String(number)) || null;
    const failed = status === 'error' || (!!data && !list);

    useEffect(() => {
        if (ticket?.ticket_category_id && ticket.ticket_category_id !== channelCategory) {
            setChannelCategory(ticket.ticket_category_id);
        }
    }, [ticket?.ticket_category_id]);

    useEffect(() => {
        // update params and switch project if ticket is not in current project
        const projectId = ticket?.ticket_category?.project_id;
        if (!projectId || projectId === selectedProject?.id) return;

        const project = projects?.find((item) => item.id === projectId);
        if (!project) return;

        searchParams.set('p', project.name.toLowerCase());
        setSearchParams(searchParams);
        switchProject(projectId);
    }, [selectedProject?.id, ticket?.ticket_category?.project_id]);

    const scrollToLatest = useCallback(() => {
        // Wait for the new entry to render before jumping the feed to it.
        requestAnimationFrame(() => {
            const el = scrollRef.current;
            if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
        });
    }, []);

    if (!ticket) {
        const isLoading = status === 'loading' || status === 'idle' || (!data && !failed);

        return (
            <div className={Style.shell}>
                <TicketDetailsNav ticket={null} />
                <div className={Style.loading}>
                    {isLoading ? (
                        <div className={Style.spinner} aria-hidden />
                    ) : (
                        <div className={Style.unavailable} role="status">
                            <span className={Style.unavailableIcon}>
                                <Icon image={failed ? 'alert-triangle' : 'search'} size={20} color="currentColor" />
                            </span>
                            <h3 className={Style.unavailableTitle}>
                                {_t('ticket.detail.unavailableTitle')}
                            </h3>
                            <p className={Style.unavailableText}>
                                {failed
                                    ? _t('ticket.detail.loadError')
                                    : _t('ticket.detail.unavailableText')}
                            </p>
                            <Button
                                text={_t('ticket.detail.retry')}
                                variant="secondary"
                                size="sm"
                                icon="refresh-cw"
                                isLoading={isFetching}
                                action={() => refetch()}
                            />
                        </div>
                    )}
                </div>
            </div>
        );
    }

    const medias = splitAttachments(ticket.attachments);

    const ticketStatus = findTicketStatus(ticket);
    const requester = ticket.histories?.find((history) => history.action_type === 'created');
    const commentCount = ticket.histories?.filter(isComment).length || 0;

    const railSections = (
        <>
            <TicketGeneralInfo ticket={ticket} status={ticketStatus} />
            <TicketOwner ticket={ticket} />
            <TicketCommands ticket={ticket} />
        </>
    );

    return (
        <div className={Style.shell}>
            <TicketDetailsNav
                ticket={ticket}
                status={ticketStatus}
                requester={requester}
                commentCount={commentCount}
            />

            <div className={Style.split}>
                <div className={Style.mainCol}>
                    {/* The only scroll in this column. */}
                    <div ref={scrollRef} className={Style.scroll}>
                        <TicketDescription ticket={ticket} />
                        <div className={Style.railInline}>
                            <TicketAttachments medias={medias} />
                            {railSections}
                        </div>
                        <TicketHistory ticket={ticket} />
                    </div>

                    <div className={Style.composer}>
                        <Chat ticket={ticket} onPosted={scrollToLatest} />
                    </div>
                </div>

                <aside className={Style.rail}>
                    <TicketAttachments medias={medias} />
                    {railSections}
                </aside>
            </div>
        </div>
    );
};
